import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ProductCodesContext = createContext({ map: {}, refresh: () => {} });

export function ProductCodesProvider({ children }) {
  const { user } = useAuth();
  // Item-code map: used ONLY for admin-visible ingredient alias prefix (CodeLabel)
  const [map, setMap] = useState({});
  // Product-code map: client-facing recipe variant IDs (TI-151, TI-151-T2 etc.)
  // Publicly visible to ALL roles (including client portal users).
  const [productMap, setProductMap] = useState({});

  const refresh = async () => {
    if (!user) return;
    try {
      const [itemRes, prodRes] = await Promise.all([
        axios.get(`${API}/product-codes/lookup-map?kind=item`, { withCredentials: true }),
        axios.get(`${API}/product-codes/lookup-map?kind=product`, { withCredentials: true }),
      ]);
      const itemR2C = itemRes.data?.real_to_code || {};
      const prodR2C = prodRes.data?.real_to_code || {};
      const normItem = {};
      Object.entries(itemR2C).forEach(([name, code]) => {
        if (name && code) normItem[String(name).trim().toLowerCase()] = { code };
      });
      const normProd = {};
      Object.entries(prodR2C).forEach(([name, code]) => {
        if (name && code) normProd[String(name).trim().toLowerCase()] = { code };
      });
      setMap(normItem);
      setProductMap(normProd);
    } catch {
      setMap({});
      setProductMap({});
    }
  };

  // Load once when user logs in; re-fetch every 5 min in case admin edits codes
  useEffect(() => {
    if (!user) { setMap({}); setProductMap({}); return; }
    refresh();
    const t = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <ProductCodesContext.Provider value={{ map, productMap, refresh }}>
      {children}
    </ProductCodesContext.Provider>
  );
}

/**
 * Hook: returns { lookup(name) -> code | '', getEntry(name) -> { code, priority, category } | null }
 */
export function useProductCodes() {
  const { map, productMap, refresh } = useContext(ProductCodesContext);
  const lookup = (name) => {
    if (!name) return '';
    const entry = map[String(name).trim().toLowerCase()];
    return entry ? entry.code : '';
  };
  const getEntry = (name) => {
    if (!name) return null;
    return map[String(name).trim().toLowerCase()] || null;
  };
  // Product-code lookup (public, client-facing)
  const lookupProduct = (name) => {
    if (!name) return '';
    const entry = productMap[String(name).trim().toLowerCase()];
    return entry ? entry.code : '';
  };
  return { map, productMap, lookup, lookupProduct, getEntry, refresh };
}
